"use client";

import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useServiceWorker } from "@/lib/pwa-config";

/**
 * Keeps the Service Worker healthy and prevents "stale app" situations.
 *
 * Premium behavior goal:
 * - No user ever needs to manually clear phone cache.
 * - If a new SW is waiting, we either auto-apply on startup or offer a 1-tap refresh.
 */
export function PWAInitializer() {
  const { updateAvailable, checkForUpdates, applyUpdate } = useServiceWorker();
  const toastShownRef = useRef(false);

  // On boot, proactively check for SW updates.
  useEffect(() => {
    void checkForUpdates();
    // Also re-check when the tab regains focus.
    const onFocus = () => void checkForUpdates();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [checkForUpdates]);

  // If an update is waiting, apply it automatically once per session *on startup*.
  // This avoids the "why didn't my fix deploy" problem without asking the user to clear cache.
  useEffect(() => {
    if (!updateAvailable) return;

    // Prevent duplicate toasts in re-renders
    if (toastShownRef.current) return;
    toastShownRef.current = true;

    const AUTO_KEY = "sb_sw_auto_update_done";
    try {
      const already = sessionStorage.getItem(AUTO_KEY);
      if (!already) {
        sessionStorage.setItem(AUTO_KEY, "1");
        toast("Updating SHOWBOLD…", {
          description: "Refreshing to the latest version.",
          duration: 2500,
        });
        // Give the toast a moment to paint.
        window.setTimeout(() => {
          void applyUpdate();
        }, 600);
        return;
      }
    } catch {
      // ignore
    }

    // Fallback: show a persistent 1-tap refresh.
    toast("Update available", {
      description: "Tap refresh to get the latest improvements.",
      duration: Infinity,
      action: {
        label: "Refresh",
        onClick: () => void applyUpdate(),
      },
    });
  }, [updateAvailable, applyUpdate]);

  return null;
}
